import {
  init as sentryInit,
  reactRouterV6BrowserTracingIntegration,
  replayIntegration,
} from '@sentry/react';
import { asyncWithLDProvider } from 'launchdarkly-react-client-sdk';
import { StrictMode, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router-dom';

import { defaultFeatureFlags } from 'SRC/hooks/useFeatureFlags/FeatureFlags';
import { initAnalytics } from 'UTILS/analytics';
import { isProduction } from 'UTILS/env';
import { isObject } from 'UTILS/isObject';

import { App } from './App';
import { appEnv, launchDarklyClientId } from './config';
import './styles';
import { FetchError } from './fetch/fetch';

/**
 * These are error IDs returned by our Backend that we can categories as "valid" errors.
 * These are not bugs and a feedback is given to the user in the UI
 * The full list of these errors can be found here: https://gitlab.com/primer-io/dashboard-backend/dashboard/-/blob/HEAD/platform/dashboard/exceptions/error_id.py
 */
const VALID_ERROR_IDS = [
  'InvalidAuthCredentials',
  'MultiFactorAuthenticationInvalidTOTPCode',
  'PaymentAlreadyRefunded',
  'PaymentMethodNotConfigured',
  'UserInviteAlreadyExists',
];

function getErrorId(originalException: unknown): string | null {
  if (!(originalException instanceof FetchError)) {
    return null;
  }

  return isObject(originalException.payload) &&
    isObject(originalException.payload.error) &&
    typeof originalException.payload.error.errorId === 'string'
    ? originalException.payload.error.errorId
    : null;
}

if (isProduction()) {
  sentryInit({
    dsn: 'https://f3b0dc8a02ab43eeba2239aa57508bdf@o515749.ingest.sentry.io/5865067',
    integrations: [
      reactRouterV6BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      replayIntegration(),
      // TODO: Enable feedback integration when we are ready to use it
      // Sentry.feedbackIntegration({
      //   colorScheme: 'light',
      //   isEmailRequired: true,
      //   isNameRequired: true,
      //   showBranding: false,
      // }),
    ],

    // Set tracesSampleRate to 1.0 to capture 100%
    // of transactions for performance monitoring.
    tracesSampleRate: 1.0,
    environment: appEnv,
    ignoreErrors: [
      /^ResizeObserver/i,
      // Chrome extensions
      /extensions\//i,
      /^chrome:\/\//i,
    ],

    // Capture Replay for 10% of all sessions,
    // plus for 100% of sessions with an error
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    /**
     * We use beforeSend to filter out valid errors before they are sent to Sentry.
     * Errors are filtered out here rather than in the place where they are thrown, for simplicity purpose.
     * This solution might or might not scale well but should be good enough to filter out those few exceptions.
     */
    beforeSend(event, { originalException }) {
      const errorId = getErrorId(originalException);
      if (errorId && VALID_ERROR_IDS.includes(errorId)) {
        return null; // Do not send event to Sentry
      }
      return event; // Default behavior: send the event to Sentry
    },
  });
}

initAnalytics();

(async () => {
  const LDProvider = await asyncWithLDProvider({
    clientSideID: launchDarklyClientId,
    deferInitialization: false,
    flags: defaultFeatureFlags,
    options: {
      sendEvents: false,
      streaming: appEnv !== 'test' && appEnv !== 'local',
    },
  });
  const container = document.getElementById('root');
  const root = createRoot(container!);
  root.render(
    <StrictMode>
      <LDProvider>
        <App />
      </LDProvider>
    </StrictMode>,
  );
})();
